import { useState, useEffect, useCallback } from "react";
import axios from "axios";

import {
  Spinner,
  Button,
  Row,
  Col,
  Modal,
  Form,
  Container,
} from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";

import { IoArrowBack } from "react-icons/io5";

import { toast } from "react-toastify";
import { ADMIN_TUTOR_API } from "../../../api/api";

import Announcementsimg from "../../../assets/announcements-empty.png";

import { initialsFromName } from "../../../utils/getInitials";
import { handleApiError } from "../../../utils/apiErrorHandler";

import "./AdminPendingTutorManagement.css";

const AdminPendingTutorManagement = () => {
  const navigate = useNavigate();
  const { tutorId } = useParams();

  const [pendingTutors, setPendingTutors] = useState([]);
  const [selectedTutor, setSelectedTutor] = useState(null);

  //Modal
  const [rejectFeedback, setRejectFeedback] = useState("");
  const [isAdminReviewModalOpen, setIsAdminReviewModalOpen] = useState(false);
  const [isAdminRejectModalOpen, setIsAdminRejectModalOpen] = useState(false);

  //loading
  const [isLoadingTutors, setIsLoadingTutors] = useState(true);

  const showErrorToast = (message) => {
    toast.error(message, {
      position: "top-right",
      autoClose: 1500,
      theme: "colored",
    });
  };

  const fetchPendingTutors = useCallback(async () => {
    const token = localStorage.getItem("token");

    if (!token) {
      setIsLoadingTutors(false);
      navigate("/admin-signin");
      return;
    }

    try {
      let response;
      if (tutorId) {
        // Fetch single tutor
        response = await axios.post(
          ADMIN_TUTOR_API.GET_TUTOR_DETAILS_BY_STATUS_ID,
          { status: "Pending", tutor_id: tutorId },
          { headers: { Authorization: `Bearer ${token}` } },
        );
      } else {
        // Fetch all pending tutors
        response = await axios.post(
          ADMIN_TUTOR_API.GET_TUTOR_DETAILS_BY_STATUS,
          { status: "Pending" },
          { headers: { Authorization: `Bearer ${token}` } },
        );
      }

      const data = response.data;

      if (data.statusCode === 200) {
        setPendingTutors(data.data || []);
      } else {
        showErrorToast(data.message);
      }
    } catch (err) {
      console.error(err);
      // Server responded with non-2xx status (like 400, 500)

      // CHECK (expiry / invalid token)
      handleApiError(err, navigate, showErrorToast, "/admin-signin");
    } finally {
      setIsLoadingTutors(false);
    }
  }, [tutorId, navigate]);

  useEffect(() => {
    fetchPendingTutors();
  }, [fetchPendingTutors]);

  const handleReviewApplication = async (tutorId) => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/admin-signin");
      return;
    }

    try {
      const response = await axios.post(
        ADMIN_TUTOR_API.GET_TUTOR_BY_ID,
        { tutor_id: tutorId },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const resData = response.data;

      if (resData.statusCode === 200) {
        const tutorData = resData.data;

        setSelectedTutor({
          tutorId: tutorData.tutor.tutor_id,
          name: tutorData.tutor.full_name,
          email: tutorData.tutor.email,
          phone: tutorData.tutor.phone_number,
          category: tutorData.tutor.subject_to_teach,
          experience: tutorData.tutor.years_of_experience,
          paymentType: tutorData.payment_plans.length
            ? tutorData.payment_plans[0].plan_type === "ROYALTY"
              ? "Royalty-Based"
              : "One-Time Settlement"
            : "N/A",
          price: tutorData.payment_plans[0]?.price || "",
          royaltyPercentage:
            tutorData.payment_plans[0]?.royalty_percentage || "",
          shortBio:
            tutorData.demo_videos.length > 0
              ? tutorData.demo_videos[0].short_bio
              : "N/A",
          professionalBackground: tutorData.tutor.professional_background || "",
          education: tutorData.education.map((edu) => ({
            educationId: edu.tutor_education_id,
            degree: edu.degree,
            institute: edu.institution,
            specialization: edu.specialization,
            year: edu.year_of_passout,
          })),
          certifications: tutorData.certificates.map((cert) => ({
            certificateId: cert.tutor_certificate_id,
            title: cert.certificate_name,
            organization: cert.issued_by,
            year: new Date(cert.date_of_issue).getFullYear(),
            file: cert.certificate_file,
          })),
          teachingStyle: tutorData.demo_videos.map(
            (video) => video.teaching_style,
          ),
          targetAudience: tutorData.demo_videos.map(
            (video) => video.student_can_expect,
          ),
          demoVideo: tutorData.demo_videos.length > 0,
          demoVideos: tutorData.demo_videos.map((video) => ({
            title: video.video_title,
            description: video.video_description,
            file: video.video_file,
          })),
        });

        setIsAdminReviewModalOpen(true);
      } else {
        showErrorToast(resData.message);
      }
    } catch (err) {
      console.error(err);
      // Server responded with non-2xx status (like 400, 500)

      // CHECK (expiry / invalid token)
      handleApiError(err, navigate, showErrorToast, "/admin-signin");
    }
  };

  const handlePublishTutorSubmit = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/admin-signin");
      return;
    }

    try {
      const response = await axios.post(
        ADMIN_TUTOR_API.UPDATE_TUTOR_STATUS,
        {
          tutor_id: selectedTutor.tutorId,
          status: "Published",
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      const data = response.data;
      if (data.statusCode === 200) {
        toast.success(data.message, {
          position: "top-right",
          autoClose: 1500,
          theme: "colored",
        });

        await fetchPendingTutors();
        setIsAdminReviewModalOpen(false);
      } else {
        showErrorToast(data.message);
      }
    } catch (err) {
      console.error(err);
      // Server responded with non-2xx status (like 400, 500)

      // CHECK (expiry / invalid token)
      handleApiError(err, navigate, showErrorToast, "/admin-signin");
    }
  };

  const handleRejectTutorSubmit = async () => {
    if (!rejectFeedback.trim()) {
      showErrorToast("Please provide rejection reason");
      return;
    }

    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/admin-signin");
      return;
    }

    try {
      const response = await axios.post(
        ADMIN_TUTOR_API.UPDATE_TUTOR_STATUS,
        {
          tutor_id: selectedTutor.tutorId,
          status: "Rejected",
          reject_reason: rejectFeedback,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      const data = response.data;

      if (data.statusCode === 200) {
        toast.success(data.message, {
          position: "top-right",
          autoClose: 1500,
          theme: "colored",
        });

        await fetchPendingTutors();
        setIsAdminRejectModalOpen(false);
        setRejectFeedback("");
      } else {
        showErrorToast(data.message);
      }
    } catch (error) {
      console.error("Error rejecting tutor:", error);
      // Server responded with non-2xx status (like 400, 500)

      // CHECK (expiry / invalid token)
      handleApiError(error, navigate, showErrorToast, "/admin-signin");
    }
  };

  return (
    <Container className="admin-tutor-pending-wrapper">
      <div className="admin-pending-backmoving-button-wrapper">
        <button
          className="admin-pending-backmoving-button-main"
          onClick={() => navigate("/header/admin/all-tutor")}
        >
          <IoArrowBack className="admin-pending-backmoving-button-icon" />
          <span className="admin-pending-backmoving-button-text">Back</span>
        </button>
      </div>
      <h4 className="admin-dashboard-title">Pending Tutor Applications</h4>
      <p className="admin-dashboard-subtitle">
        Review tutor applications and approve eligible applicants to provide
        dashboard access.
      </p>

      {isLoadingTutors ? (
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : pendingTutors.length > 0 ? (
        pendingTutors.map((tutor) => (
          <div className="admin-tutor-pending-card mb-4" key={tutor.tutor_id}>
            {/* HEADER */}
            <div className="admin-tutor-pending-header">
              <h5 className="admin-tutor-published-header-title">
                {tutor.full_name || "N/A"}
                <p className="admin-tutor-published-header-para">
                  {tutor.subject_to_teach || "N/A"}
                </p>
              </h5>
              <span
                className={`admin-tutor-pending-status admin-tutor-pending-${tutor.status?.toLowerCase()}`}
              >
                {tutor.status}
              </span>
            </div>

            {/* INFO GRID */}
            <div className="admin-tutor-pending-info-grid">
              <div className="admin-tutor-pending-info-box">
                <p>Email</p>
                <h6>{tutor.email || "N/A"}</h6>
              </div>

              <div className="admin-tutor-pending-info-box">
                <p>Experience</p>
                <h6>{tutor.experience || "N/A"}</h6>
              </div>

              <div className="admin-tutor-pending-info-box">
                <p>Certifications</p>
                <h6>{tutor.certifications || "N/A"}</h6>
              </div>

              <div className="admin-tutor-pending-info-box">
                <p>Education</p>
                <h6>{tutor.education || "N/A"}</h6>
              </div>
            </div>

            {/* DESCRIPTION */}
            <Row className="admin-tutor-pending-description">
              <Col md={3}>
                <div className="admin-tutor-pending-info-box admin-tutor-pending-box-equal">
                  <p>Submitted On</p>
                  <h6>{tutor.submitted_at || "N/A"}</h6>
                </div>
              </Col>

              {/* Bigger Box */}
              <Col md={9}>
                <div className="admin-tutor-pending-info-box admin-tutor-pending-box-equal">
                  <p>Short Bio</p>
                  <h6>{tutor.short_bio || "No bio available"}</h6>
                </div>
              </Col>
            </Row>

            {/* PRICE + COUNTRY */}
            <div className="admin-tutor-pending-info-box d-flex mt-3">
              <Col md={6}>
                <p>Price</p>
                <h6>{tutor.price || "N/A"}</h6>
              </Col>

              <Col md={6}>
                <p>Plan Type</p>
                <h6>
                  {tutor.plan_type === "ROYALTY"
                    ? "Royalty-Based"
                    : tutor.plan_type
                      ? "One-Time Settlement"
                      : "N/A"}
                </h6>
              </Col>
            </div>

            <div className="admin-tutor-pending-action mt-4 text-end">
              <Button
                className="admin-tutor-pending-review"
                onClick={() => handleReviewApplication(tutor.tutor_id)}
              >
                Review Application
              </Button>
            </div>
          </div>
        ))
      ) : (
        <div className="tutor-assignment-empty-box">
          <div className="tutor-assignment-empty-content">
            <img
              src={Announcementsimg}
              alt="empty"
              className="tutor-announcements-section-empty-img mb-3"
            />
            <h5>No pending tutors found.</h5>
          </div>
        </div>
      )}

      <Modal
        show={isAdminReviewModalOpen}
        onHide={() => setIsAdminReviewModalOpen(false)}
        size="lg"
        centered
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton className="admin-review-modal-header-part">
          <div className="admin-tutor-review-header-text-wrapper">
            <Modal.Title className="admin-review-modal-header-title">
              Review Tutor Application
            </Modal.Title>
            <p className="admin-review-modal-header-paragraph">
              Review the submitted tutor application, verify the provided
              information, and approve or reject it.
            </p>
          </div>
        </Modal.Header>

        <Modal.Body className="admin-tutor-review-modal-body">
          {selectedTutor ? (
            <>
              <div className="admin-tutor-review-profile-card">
                <div className="admin-tutor-review-profile-avatar">
                  {initialsFromName(selectedTutor.name)}
                </div>

                <div className="admin-tutor-profile-info">
                  <div>
                    <h6>{selectedTutor.name}</h6>
                    <p>{selectedTutor.category}</p>
                  </div>
                  <div className="admin-tutor-review-profile-meta">
                    <span>{selectedTutor.experience}</span>
                    <span>{selectedTutor.paymentType}</span>
                    <span>
                      {selectedTutor.paymentType === "Royalty-Based"
                        ? `${selectedTutor.price} (${selectedTutor.royaltyPercentage}% Royalty)`
                        : `${selectedTutor.price}`}
                    </span>
                  </div>
                </div>
              </div>

              <div className="admin-tutor-review-review-section">
                <h6>Short Bio</h6>
                <p>{selectedTutor.shortBio}</p>
              </div>

              <div className="admin-tutor-review-review-grid">
                <div className="admin-tutor-review-review-grid-1">
                  <label>Email</label>
                  <p>{selectedTutor.email}</p>
                </div>
                <div className="admin-tutor-review-review-grid-2">
                  <label>Phone</label>
                  <p>{selectedTutor.phone}</p>
                </div>
              </div>

              <div className="admin-tutor-review-review-section">
                <h6>Professional Background</h6>
                <p>{selectedTutor.professionalBackground}</p>
              </div>

              <div className="admin-tutor-review-review-section">
                <h6>Education ({selectedTutor.education.length})</h6>
                <div className="admin-tutor-review-review-card-grid">
                  {selectedTutor.education.map((edu) => (
                    <div
                      className="admin-tutor-review-mini-card"
                      key={edu.educationId}
                    >
                      <b>{edu.degree}</b>
                      <span>
                        {edu.institute} ({edu.year})
                      </span>
                      <small>{edu.specialization}</small>
                    </div>
                  ))}
                </div>
              </div>

              <div className="admin-tutor-review-review-section">
                <h6>Certifications ({selectedTutor.certifications.length})</h6>
                <div className="admin-tutor-review-review-card-grid">
                  {selectedTutor.certifications.map((cert) => (
                    <div
                      className="admin-tutor-review-mini-card"
                      key={cert.certificateId}
                    >
                      <b>{cert.title}</b>
                      <span>
                        {cert.organization}{" "}
                        <a
                          href={cert.file}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="admin-tutor-review-certificate-link"
                        >
                          (Certificate Link)
                        </a>
                      </span>
                      <small>{cert.year}</small>
                    </div>
                  ))}
                </div>
              </div>

              <div className="admin-tutor-review-review-section">
                <h6>Teaching Approach</h6>
                <div className="admin-tutor-review-pill-group">
                  {selectedTutor.teachingStyle.map((item, i) => (
                    <span key={i} className="admin-tutor-review-pill">
                      {item}
                    </span>
                  ))}
                </div>
              </div>

              <div className="admin-tutor-review-review-sectionssss">
                <h6>What Students Can Expect</h6>
                <div className="admin-tutor-review-pill-group-audience">
                  {selectedTutor.targetAudience.map((item, i) => (
                    <span
                      key={i}
                      className="admin-tutor-pending-review-pilling"
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </div>

              <div className="admin-tutor-review-review-section">
                <h6>Demo Video</h6>
                {selectedTutor.demoVideo &&
                selectedTutor.demoVideos?.length > 0 ? (
                  <div className="admin-tutor-pending-demo-video-section">
                    <h6 className="admin-tutor-pending-demo-video-title">
                      {selectedTutor.demoVideos[0].title}
                    </h6>

                    <p className="admin-tutor-pending-demo-video-description">
                      {selectedTutor.demoVideos[0].description}
                    </p>
                    <div className="admin-tutor-review-demo-preview-box">
                      <video
                        src={selectedTutor.demoVideos[0].file}
                        controls
                        controlsList="nodownload"
                        width="100%"
                        className="admin-tutor-pending-review-demo-video"
                      >
                        Your browser does not support the video tag.
                      </video>
                    </div>
                  </div>
                ) : (
                  "No demo video uploaded"
                )}
              </div>
            </>
          ) : (
            <p>Loading tutor details...</p>
          )}
        </Modal.Body>

        <Modal.Footer className="admin-review-modal-footer">
          <Button
            variant="success"
            onClick={handlePublishTutorSubmit}
            className="admin-review-modal-footer-button-approve"
          >
            Approve Tutor
          </Button>
          <Button
            variant="danger"
            onClick={() => {
              setIsAdminReviewModalOpen(false);
              setTimeout(() => setIsAdminRejectModalOpen(true), 100);
            }}
            className="admin-review-modal-footer-button-reject"
          >
            Reject Application
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={isAdminRejectModalOpen}
        centered
        onHide={() => setIsAdminRejectModalOpen(false)}
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header
          closeButton
          className="admin-tutor-review-header-model222"
        >
          <div className="admin-tutor-review-header-text-wrapper">
            <Modal.Title className="admin-review-modal-header-title">
              Reject Tutor Application
            </Modal.Title>
            <p className="admin-review-modal-header-paragraph">
              Provide a reason and detailed feedback for rejecting the tutor
              application
            </p>
          </div>
        </Modal.Header>

        <Modal.Body className="admin-tutor-pending-reject-model">
          {selectedTutor && (
            <>
              <div className="admin-tutor-pending-reject-tutor-profile-box">
                <div className="admin-tutor-pending-reject-tutor-avatar">
                  {initialsFromName(selectedTutor.name)}
                </div>

                <div className="admin-tutor-pending-reject-tutor-name">
                  <h6>{selectedTutor.name}</h6>
                  <p>{selectedTutor.category}</p>
                </div>
              </div>
              <div className="admin-tutor-pending-reject-tutor-meta-two">
                <div className="admin-tutor-pending-reject-tutor-meta">
                  <h6>Experience</h6>
                  <span>{selectedTutor.experience}</span>
                </div>

                <div className="admin-tutor-pending-reject-tutor-meta">
                  <h6>Payment</h6>
                  <span>{selectedTutor.paymentType}</span>
                </div>
              </div>
              <div className="admin-tutor-pending-reject-tutor-meta">
                <h6>Amount</h6>
                <span>{selectedTutor.price}</span>
              </div>
            </>
          )}

          <Form.Group className="mt-3">
            <Form.Label>Rejection Feedback</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Example: Missing documents / Video quality is poor / Incorrect information provided"
              value={rejectFeedback}
              onChange={(e) => setRejectFeedback(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>

        <Modal.Footer className="admin-tutor-pending-reject-tutor-meta-footer">
          <Button
            variant="danger"
            onClick={handleRejectTutorSubmit}
            className="admin-reject-tutor-meta-footer-bnt-reject"
          >
            Reject Tutor
          </Button>

          <Button
            variant="secondary"
            onClick={() => setIsAdminRejectModalOpen(false)}
            className="admin-reject-tutor-meta-footer-bnt-cancel"
          >
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AdminPendingTutorManagement;
